import { buildClasses } from "../utils/buildClasses";
import { RyeIcon } from "./RyeIcon";
import { RyeVariant, RyeVibe } from "./RyeConsts";

// A badge's own variants: the shared ones, plus "soft", a borderless tint for
// quiet badges like a footer's stats.
export type RyeBadgeVariant = RyeVariant | "soft";

type RyeBadgeSize = "sm" | "md" | "lg";

// Set on each of the badge's own text elements too, as a global style gives
// spans and divs a font size of their own rather than inheriting it.
const TEXT_SIZE = new Map<RyeBadgeSize, string>([
  ["sm", "text-sm"],
  ["md", "text-md"],
  ["lg", "text-lg"],
]);

export function RyeBadge({
  label,
  text,
  icon,
  children,
  variant = "outlined",
  vibe = "ordinary",
  size = "md",
  shape = "rounded-rect",
  shadow = "none",
  highlightOnHover = false,
  className = "",
}: {
  /** A quiet label leading the badge's value, e.g. "Count" before "312". */
  label?: string;
  text?: string;
  icon?: string;
  children?: React.ReactNode;
  size?: RyeBadgeSize;
  shape?: "rounded-rect" | "fully-round";
  shadow?: "none" | "sm" | "md" | "lg";
  highlightOnHover?: boolean;
  variant?: RyeBadgeVariant;
  vibe?: RyeVibe;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={
        buildClasses(
          // Size
          {
            switch: size,
            cases: new Map([
              [
                "sm",
                buildClasses(
                  {
                    if: text === undefined && !children,
                    then: "w-7",
                    else: "px-2.5",
                  },
                  "h-7",
                ),
              ],
              [
                "md",
                buildClasses(
                  {
                    if: text === undefined && !children,
                    then: "w-9",
                    else: "px-3",
                  },
                  "h-9",
                ),
              ],
              [
                "lg",
                buildClasses(
                  {
                    if: text === undefined && !children,
                    then: "w-11",
                    else: "px-3.5",
                  },
                  "h-11",
                ),
              ],
            ]),
          },
          { switch: size, cases: TEXT_SIZE },
          // Width handling for nested badges
          { if: children !== undefined, then: "inline-flex min-w-fit w-auto" },
          { if: children === undefined, then: "inline-flex" },

          // Shape
          {
            switch: shape,
            cases: new Map([
              ["rounded-rect", buildClasses("rounded-md")],
              ["fully-round", buildClasses("rounded-full")],
            ]),
          },
          // Shadow
          {
            switch: shadow,
            cases: new Map([
              ["none", buildClasses("shadow-none")],
              ["sm", buildClasses("shadow-sm")],
              ["md", buildClasses("shadow-md")],
              ["lg", buildClasses("shadow-lg")],
            ]),
          },
          // Variant & Vibe
          {
            switch: variant,
            cases: new Map([
              [
                "filled",
                buildClasses({
                  switch: vibe,
                  cases: new Map([
                    [
                      "subdued",
                      buildClasses(
                        "bg-on-surface-200",
                        "text-on-surface-600",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-on-surface-300",
                        }),
                      ),
                    ],
                    [
                      "ordinary",
                      buildClasses(
                        "bg-on-surface-400",
                        "text-on-surface-50",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-on-surface-500",
                        }),
                      ),
                    ],
                    [
                      "primary",
                      buildClasses(
                        "bg-purple",
                        "text-on-surface-50",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-purple-600",
                        }),
                      ),
                    ],
                    [
                      "danger",
                      buildClasses(
                        "bg-red",
                        "text-on-surface-50",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-red-600",
                        }),
                      ),
                    ],
                  ]),
                }),
              ],
              [
                "outlined",
                buildClasses({
                  switch: vibe,
                  cases: new Map([
                    [
                      "subdued",
                      buildClasses(
                        "border",
                        "border-on-surface-200",
                        "text-on-surface-500",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-on-surface-100",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:border-on-surface-300",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:text-on-surface-600",
                        }),
                      ),
                    ],
                    [
                      "ordinary",
                      buildClasses(
                        "border",
                        "border-on-surface-300",
                        "bg-on-surface-100",
                        "text-on-surface-600",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-on-surface-200",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:border-on-surface-400",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:text-on-surface-700",
                        }),
                      ),
                    ],
                    [
                      "primary",
                      buildClasses(
                        "border",
                        "border-purple",
                        "bg-purple-50",
                        "text-purple",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-purple-100",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:border-purple-600",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:text-purple-600",
                        }),
                      ),
                    ],
                    [
                      "danger",
                      buildClasses(
                        "border",
                        "border-red",
                        "bg-red-100",
                        "text-red",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-red-200",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:border-red-600",
                        }),
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:text-red-600",
                        }),
                      ),
                    ],
                  ]),
                }),
              ],
              [
                "soft",
                buildClasses({
                  switch: vibe,
                  cases: new Map([
                    ["subdued", buildClasses("bg-well", "text-on-surface-500")],
                    [
                      "ordinary",
                      buildClasses("bg-well", "text-on-surface-700"),
                    ],
                    [
                      "primary",
                      buildClasses("bg-purple-50", "text-purple-600"),
                    ],
                    ["danger", buildClasses("bg-red-50", "text-red-600")],
                  ]),
                }),
              ],
              [
                "transparent",
                buildClasses({
                  switch: vibe,
                  cases: new Map([
                    [
                      "subdued",
                      buildClasses(
                        "text-on-surface-500",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-on-surface-200",
                        }),
                      ),
                    ],
                    [
                      "ordinary",
                      buildClasses(
                        "text-on-surface-700",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-on-surface-200",
                        }),
                      ),
                    ],
                    [
                      "primary",
                      buildClasses(
                        "text-purple",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-purple-100",
                        }),
                      ),
                    ],
                    [
                      "danger",
                      buildClasses(
                        "text-red",
                        buildClasses({
                          if: highlightOnHover,
                          then: "hover:bg-red-100",
                        }),
                      ),
                    ],
                  ]),
                }),
              ],
            ]),
          },
          // Base
          "flex",
          "items-center",
          "justify-center",
          "select-none",
          "focus:outline-none",
          "transition-all",
          "duration-75",
        ) +
        " " +
        className
      }
    >
      <BadgeContent
        label={label}
        text={text}
        icon={icon}
        size={size}
        vibe={vibe}
      >
        {children}
      </BadgeContent>
    </div>
  );
}

function BadgeContent({
  label,
  text,
  icon,
  size,
  vibe,
  children,
}: {
  label?: string;
  text?: string;
  icon?: string;
  size: RyeBadgeSize;
  vibe: RyeVibe;
  children?: React.ReactNode;
}): JSX.Element {
  const textSize = TEXT_SIZE.get(size) ?? "";
  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "justify-center",
        "w-full", // Ensure the content takes full width of parent
        "min-w-fit", // Ensure minimum width fits content
        {
          switch: size,
          cases: new Map([
            ["sm", buildClasses("gap-1.5")],
            ["md", buildClasses("gap-2")],
            ["lg", buildClasses("gap-2.5")],
          ]),
        },
      )}
    >
      {icon && <RyeIcon name={icon} size={size} />}
      {label && (
        <span
          className={buildClasses("whitespace-nowrap", textSize, {
            switch: vibe,
            cases: new Map([
              ["subdued", "text-on-surface-400"],
              ["ordinary", "text-on-surface-400"],
              ["primary", "text-purple-400"],
              ["danger", "text-red-400"],
            ]),
          })}
        >
          {label}
        </span>
      )}
      {text && (
        <span className={buildClasses("truncate", textSize)}>{text}</span>
      )}
      {children && (
        <div
          className={buildClasses(
            "flex-1",
            "flex",
            "items-center",
            "gap-1",
            "min-w-0",
            "overflow-hidden",
            textSize,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
